/** Stable customer profile address slots (1–5). Deleting a middle slot does not renumber later ones. */

export const MAX_CUSTOMER_ADDRESS_SLOTS = 5;

export const emptyCustomerAddress = (slot = 1) => ({
  slot,
  line1: "",
  line2: "",
  city: "",
  state: "",
  zipCode: "",
  note: "",
});

export const getCustomerAddressBySlot = (addresses = [], slot) =>
  (addresses || []).find((address) => Number(address?.slot) === Number(slot));

/** First unused slot number in 1..MAX, or null if all filled. */
export const findNextCustomerAddressSlot = (addresses = []) => {
  const used = new Set((addresses || []).map((address) => Number(address?.slot)));
  for (let slot = 1; slot <= MAX_CUSTOMER_ADDRESS_SLOTS; slot += 1) {
    if (!used.has(slot)) return slot;
  }
  return null;
};

export const sortCustomerAddressesBySlot = (addresses = []) =>
  [...(addresses || [])].sort((a, b) => Number(a?.slot) - Number(b?.slot));

/**
 * Build payload fields for address slots 1–5 from slot-stable UI state.
 * Missing slots are cleared to empty strings so deleted middle slots stay empty in DB.
 */
export const buildCustomerAddressSaveFields = (addresses = []) => {
  const bySlot = (slot) => getCustomerAddressBySlot(addresses, slot);
  const a1 = bySlot(1);
  const a2 = bySlot(2);
  const a3 = bySlot(3);
  const a4 = bySlot(4);
  const a5 = bySlot(5);

  return {
    address_line_1: a1?.line1 || "",
    address_line_2: a1?.line2 || "",
    city: a1?.city || "",
    state: a1?.state || "",
    zip_code: a1?.zipCode || "",
    address_note: a1?.note || "",
    address1: `${a1?.line1 || ""}, ${a1?.line2 || ""}, ${a1?.city || ""}, ${a1?.state || ""} ${a1?.zipCode || ""}`
      .replace(/, ,/g, ",")
      .trim(),
    street_address_1: a1?.line1 || "",
    city1: a1?.city || "",
    state1: a1?.state || "",
    zip_code1: a1?.zipCode || "",

    address2_line_1: a2?.line1 || "",
    address2_line_2: a2?.line2 || "",
    city2: a2?.city || "",
    state2: a2?.state || "",
    zip_code2: a2?.zipCode || "",
    address2_note: a2?.note || "",
    street_address_2: a2?.line1 || "",
    address2: a2
      ? `${a2.line1 || ""}, ${a2.line2 || ""}, ${a2.city || ""}, ${a2.state || ""} ${a2.zipCode || ""}`
          .replace(/, ,/g, ",")
          .trim()
      : "",

    address3_line_1: a3?.line1 || "",
    address3_line_2: a3?.line2 || "",
    city3: a3?.city || "",
    state3: a3?.state || "",
    zip_code3: a3?.zipCode || "",
    address3_note: a3?.note || "",
    street_address_3: a3?.line1 || "",
    address3: a3
      ? `${a3.line1 || ""}, ${a3.line2 || ""}, ${a3.city || ""}, ${a3.state || ""} ${a3.zipCode || ""}`
          .replace(/, ,/g, ",")
          .trim()
      : "",

    address4_line_1: a4?.line1 || "",
    address4_line_2: a4?.line2 || "",
    city4: a4?.city || "",
    state4: a4?.state || "",
    zip_code4: a4?.zipCode || "",
    address4_note: a4?.note || "",
    street_address_4: a4?.line1 || "",
    address4: a4
      ? `${a4.line1 || ""}, ${a4.line2 || ""}, ${a4.city || ""}, ${a4.state || ""} ${a4.zipCode || ""}`
          .replace(/, ,/g, ",")
          .trim()
      : "",

    address5_line_1: a5?.line1 || "",
    address5_line_2: a5?.line2 || "",
    city5: a5?.city || "",
    state5: a5?.state || "",
    zip_code5: a5?.zipCode || "",
    address5_note: a5?.note || "",
    street_address_5: a5?.line1 || "",
    address5: a5
      ? `${a5.line1 || ""}, ${a5.line2 || ""}, ${a5.city || ""}, ${a5.state || ""} ${a5.zipCode || ""}`
          .replace(/, ,/g, ",")
          .trim()
      : "",
  };
};

/** Load slot-stable address list from a customer document (keeps gaps). */
export const loadCustomerAddressesFromProfile = (customer) => {
  if (!customer) return [emptyCustomerAddress(1)];
  const loaded = [];

  if (customer.address_line_1 || customer.street_address_1 || customer.city || customer.city1) {
    loaded.push({
      slot: 1,
      line1: customer.address_line_1 || customer.street_address_1 || "",
      line2: customer.address_line_2 || customer.apartment || "",
      city: customer.city || customer.city1 || "",
      state: customer.state || customer.state1 || "",
      zipCode: customer.zip_code || customer.zip_code1 || "",
      note: customer.address_note || "",
    });
  }
  if (customer.address2_line_1 || customer.street_address_2 || customer.city2) {
    loaded.push({
      slot: 2,
      line1: customer.address2_line_1 || customer.street_address_2 || "",
      line2: customer.address2_line_2 || "",
      city: customer.city2 || "",
      state: customer.state2 || "",
      zipCode: customer.zip_code2 || "",
      note: customer.address2_note || "",
    });
  }
  if (customer.address3_line_1 || customer.street_address_3 || customer.city3) {
    loaded.push({
      slot: 3,
      line1: customer.address3_line_1 || customer.street_address_3 || "",
      line2: customer.address3_line_2 || "",
      city: customer.city3 || "",
      state: customer.state3 || "",
      zipCode: customer.zip_code3 || "",
      note: customer.address3_note || "",
    });
  }
  if (customer.address4_line_1 || customer.street_address_4 || customer.city4) {
    loaded.push({
      slot: 4,
      line1: customer.address4_line_1 || customer.street_address_4 || "",
      line2: customer.address4_line_2 || "",
      city: customer.city4 || "",
      state: customer.state4 || "",
      zipCode: customer.zip_code4 || "",
      note: customer.address4_note || "",
    });
  }
  if (customer.address5_line_1 || customer.street_address_5 || customer.city5) {
    loaded.push({
      slot: 5,
      line1: customer.address5_line_1 || customer.street_address_5 || "",
      line2: customer.address5_line_2 || "",
      city: customer.city5 || "",
      state: customer.state5 || "",
      zipCode: customer.zip_code5 || "",
      note: customer.address5_note || "",
    });
  }

  if (loaded.length === 0) {
    return [emptyCustomerAddress(1)];
  }
  return sortCustomerAddressesBySlot(loaded);
};
